// Google Fonts utilities and popular font list

export interface GoogleFont {
  name: string;
  family: string;
  category: 'sans-serif' | 'serif' | 'display' | 'handwriting' | 'monospace';
  variants?: string[];
}

// Popular Google Fonts list
export const GOOGLE_FONTS: GoogleFont[] = [
  // Sans-serif fonts
  { name: 'Inter', family: 'Inter, ui-sans-serif, system-ui, sans-serif', category: 'sans-serif' },
  { name: 'Roboto', family: 'Roboto, ui-sans-serif, system-ui, sans-serif', category: 'sans-serif' },
  { name: 'Open Sans', family: '"Open Sans", ui-sans-serif, system-ui, sans-serif', category: 'sans-serif' },
  { name: 'Lato', family: 'Lato, ui-sans-serif, system-ui, sans-serif', category: 'sans-serif' },
  { name: 'Montserrat', family: 'Montserrat, ui-sans-serif, system-ui, sans-serif', category: 'sans-serif' },
  { name: 'Source Sans Pro', family: '"Source Sans Pro", ui-sans-serif, system-ui, sans-serif', category: 'sans-serif' },
  { name: 'Nunito', family: 'Nunito, ui-sans-serif, system-ui, sans-serif', category: 'sans-serif' },
  { name: 'Poppins', family: 'Poppins, ui-sans-serif, system-ui, sans-serif', category: 'sans-serif' },
  { name: 'Work Sans', family: '"Work Sans", ui-sans-serif, system-ui, sans-serif', category: 'sans-serif' },
  { name: 'Raleway', family: 'Raleway, ui-sans-serif, system-ui, sans-serif', category: 'sans-serif' },

  // Serif fonts
  { name: 'Playfair Display', family: '"Playfair Display", ui-serif, Georgia, serif', category: 'serif' },
  { name: 'Merriweather', family: 'Merriweather, ui-serif, Georgia, serif', category: 'serif' },
  { name: 'Libre Baskerville', family: '"Libre Baskerville", ui-serif, Georgia, serif', category: 'serif' },
  { name: 'Lora', family: 'Lora, ui-serif, Georgia, serif', category: 'serif' },
  { name: 'PT Serif', family: '"PT Serif", ui-serif, Georgia, serif', category: 'serif' },
  { name: 'Crimson Text', family: '"Crimson Text", ui-serif, Georgia, serif', category: 'serif' },
  { name: 'Cormorant Garamond', family: '"Cormorant Garamond", ui-serif, Georgia, serif', category: 'serif' },

  // Display fonts
  { name: 'Oswald', family: 'Oswald, ui-sans-serif, system-ui, sans-serif', category: 'display' },
  { name: 'Bebas Neue', family: '"Bebas Neue", ui-sans-serif, system-ui, sans-serif', category: 'display' },
  { name: 'Anton', family: 'Anton, ui-sans-serif, system-ui, sans-serif', category: 'display' },
  { name: 'Righteous', family: 'Righteous, ui-sans-serif, system-ui, sans-serif', category: 'display' },
  { name: 'Fredoka One', family: '"Fredoka One", ui-sans-serif, system-ui, sans-serif', category: 'display' },
  { name: 'Audiowide', family: 'Audiowide, ui-sans-serif, system-ui, sans-serif', category: 'display' },
  { name: 'Boldonse', family: 'Boldonse, ui-sans-serif, system-ui, sans-serif', category: 'display' },
  { name: 'Borel', family: 'Borel, cursive', category: 'display' },
  { name: 'Bruno Ace SC', family: '"Bruno Ace SC", ui-sans-serif, system-ui, sans-serif', category: 'display' },
  { name: 'Luckiest Guy', family: '"Luckiest Guy", ui-sans-serif, system-ui, sans-serif', category: 'display' },
  { name: 'Monofett', family: 'Monofett, ui-sans-serif, system-ui, sans-serif', category: 'display' },
  { name: 'Tomorrow', family: 'Tomorrow, ui-sans-serif, system-ui, sans-serif', category: 'display' },

  // Handwriting fonts
  { name: 'Dancing Script', family: '"Dancing Script", cursive', category: 'handwriting' },
  { name: 'Pacifico', family: 'Pacifico, cursive', category: 'handwriting' },
  { name: 'Great Vibes', family: '"Great Vibes", cursive', category: 'handwriting' },
  { name: 'Amatic SC', family: '"Amatic SC", cursive', category: 'handwriting' },
  { name: 'Meow Script', family: '"Meow Script", cursive', category: 'handwriting' },
  { name: 'Petit Formal Script', family: '"Petit Formal Script", cursive', category: 'handwriting' },
  { name: 'Rouge Script', family: '"Rouge Script", cursive', category: 'handwriting' },
  { name: 'Walter Turncoat', family: '"Walter Turncoat", cursive', category: 'handwriting' },

  // Monospace fonts
  { name: 'Fira Code', family: '"Fira Code", ui-monospace, monospace', category: 'monospace' },
  { name: 'JetBrains Mono', family: '"JetBrains Mono", ui-monospace, monospace', category: 'monospace' },
  { name: 'Source Code Pro', family: '"Source Code Pro", ui-monospace, monospace', category: 'monospace' },
];

const HOME_HEADING_OPTICAL_OFFSETS: Record<string, string> = {
  Borel: '0.12em',
};

const HEADING_TOP_OFFSETS: Record<string, string> = {
  Borel: '0.5rem',
};

/**
 * Resolve an optical Y-shift for Inicio heading based on active heading font.
 */
export function getHomeHeadingOpticalOffset(fontFamilyString: string | undefined): string {
  if (!fontFamilyString) return '0em';

  const fontName = fontFamilyString.split(',')[0]?.trim().replace(/['"]/g, '');
  if (!fontName) return '0em';

  const directMatch = HOME_HEADING_OPTICAL_OFFSETS[fontName];
  if (directMatch) return directMatch;

  const normalizedFontName = fontName.toLowerCase();
  const partialMatch = Object.entries(HOME_HEADING_OPTICAL_OFFSETS).find(([key]) =>
    normalizedFontName.includes(key.toLowerCase()),
  );
  return partialMatch?.[1] ?? '0em';
}

export function getHeadingTopOffset(fontFamilyString: string | undefined): string {
  if (!fontFamilyString) return '0px';

  const fontName = fontFamilyString.split(',')[0]?.trim().replace(/['"]/g, '');
  if (!fontName) return '0px';

  const directMatch = HEADING_TOP_OFFSETS[fontName];
  if (directMatch) return directMatch;

  const normalizedFontName = fontName.toLowerCase();
  const partialMatch = Object.entries(HEADING_TOP_OFFSETS).find(([key]) =>
    normalizedFontName.includes(key.toLowerCase()),
  );
  return partialMatch?.[1] ?? '0px';
}

/**
 * Generate Google Fonts URL for multiple fonts
 */
export function generateGoogleFontsUrl(fontNames: string[]): string {
  const fontQueries = fontNames
    .map(name => {
      const font = GOOGLE_FONTS.find(f => f.name === name);
      if (!font) return null;

      // Convert font name to Google Fonts format
      const formattedName = name.replace(/\s+/g, '+');
      return `family=${formattedName}:wght@400;500;600;700`;
    })
    .filter(Boolean);

  if (fontQueries.length === 0) return '';

  return `https://fonts.googleapis.com/css2?${fontQueries.join('&')}&display=swap`;
}

/**
 * Convert HSL color to HEX
 */
export function hslToHex(hsl: string): string {
  // Parse HSL string like "348 87% 91%"
  const matches = hsl.match(/(\d+(?:\.\d+)?)\s+(\d+(?:\.\d+)?)%\s+(\d+(?:\.\d+)?)%/);
  if (!matches) return '#000000';

  const h = parseFloat(matches[1]) / 360;
  const s = parseFloat(matches[2]) / 100;
  const l = parseFloat(matches[3]) / 100;

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + (q - p) * 6 * t;
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
    return p;
  };

  let r, g, b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    const p = 2 * l - q;
    r = hue2rgb(p, q, h + 1 / 3);
    g = hue2rgb(p, q, h);
    b = hue2rgb(p, q, h - 1 / 3);
  }

  const toHex = (c: number) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };

  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}
